import React from 'react';
import { Box, ListPrice, Pricing, ShortTaxMessage, SkeletonText, SkeletonTextLine } from '@vp/swan';
import { usePricingContextIfProvided } from '../../hooks/usePricingContextIfProvided';
import { useVatMessaging } from '../../hooks/useVatMessaging';
import { priceFromCents } from '../../utils/priceFromCents';
import type { PriceType } from '../../types';

export type PriceProps = {
    price?: PriceType;
    loading?: boolean;
};

export function Price({ price, loading = false }: PriceProps): React.ReactNode {
    if (!price && !loading) {
        return null;
    }

    return (
        <Box as="span" data-testid="price-container" aria-live="polite" aria-busy={loading}>
            <PriceContent price={price} loading={loading} />
        </Box>
    );
}

type PriceContentProps = {
    price: PriceType | undefined;
    loading: boolean;
};

function PriceContent({ price, loading }: PriceContentProps) {
    const pricingContext = usePricingContextIfProvided();
    const vatMessage = useVatMessaging();

    if (loading) {
        return (
            <SkeletonText data-testid="price-skeleton">
                <SkeletonTextLine width="narrow" />
            </SkeletonText>
        );
    }

    if (!price) {
        return null;
    }

    const amount = pricingContext?.vatInclusive ? price.listPrice.taxed : price.listPrice.untaxed;
    const formattedPrice = priceFromCents({ amount, currency: price.currency });

    return (
        <Pricing>
            <ListPrice>{formattedPrice}</ListPrice>
            {vatMessage && <ShortTaxMessage>{` (${vatMessage})`}</ShortTaxMessage>}
        </Pricing>
    );
}
